import { VisualizationContainer } from "@/components/flow/visualization-container";
import { usePublicSession } from "@/lib/context/public-session-context";
import { useInstancePortfolioCosts } from "@/lib/hooks/use-flow-instances/use-cost-overrides";
import { useT } from "@/lib/i18n/use-t";
import { useCostOverrideEditor } from "../cost-override";
import type { PortfolioCost } from "../flow-wizard/flow-wizard-types";
import { PortfolioCostsWidget } from "../portfolio-costs-widget";
import { T } from "./portfolio-costs.i18n";

type PortfolioCostsProps = {
  /** The collection's fee rows from the flow definition; shown until the instance read lands. */
  portfolioCosts: PortfolioCost[];
  config: Record<string, unknown>;
  currency: string;
  /** QL-20 — the advisor's instance: its fee rows carry the overrides, and are editable. */
  instanceId?: string | null;
  /** QL-20 — a locked instance (or read-only viewer) shows overrides but never edits. */
  locked?: boolean;
};

export function PortfolioCosts({
  portfolioCosts,
  config,
  currency,
  instanceId = null,
  locked = false,
}: PortfolioCostsProps) {
  const t = useT(T);
  // Client flow: the instance's fee rows are read through the session.
  const { isPublic } = usePublicSession();
  const { portfolioCosts: instanceCosts } = useInstancePortfolioCosts(instanceId);
  const editor = useCostOverrideEditor(isPublic ? null : instanceId, { locked });
  const costs = instanceCosts ?? portfolioCosts;

  if (costs.length === 0) {
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.noPortfolioCostsConfigured}
      </div>
    );
  }

  return (
    <VisualizationContainer config={config}>
      <PortfolioCostsWidget
        portfolioCosts={costs}
        currency={currency}
        onEditCost={editor.canReset && instanceCosts ? editor.edit : undefined}
        canOverride={editor.canEdit}
      />
      {editor.dialog}
    </VisualizationContainer>
  );
}
